import {DestroyRef, inject, Injectable} from '@angular/core';
import {takeUntilDestroyed, toObservable} from '@angular/core/rxjs-interop';
import {combineLatest, distinctUntilChanged, filter, map} from 'rxjs';
import {environment} from '../../../environments/environment';
import {AuthorizationService} from '../authorization.service';
import {StateService} from '../state.service';
import {PolarisMigrationScope} from './model';
import {PolarisRuntimeFacadeService} from './polaris-runtime-facade.service';

@Injectable({
  providedIn: 'root'
})
export class PolarisRuntimeBootstrapService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly stateService = inject(StateService);
  private readonly polarisRuntimeFacadeService = inject(PolarisRuntimeFacadeService);

  private started = false;
  private initializedScopeKey: string | null = null;
  private inFlightScopeKey: string | null = null;

  public start(): void {
    if (this.started) {
      return;
    }
    this.started = true;

    combineLatest([
      toObservable(this.stateService.activeAccount),
      this.authorizationService.userProfile$
    ])
      .pipe(
        map(([accountKey, profile]) => ({
          accountKey,
          userId: profile.id
        })),
        filter(scope => Boolean(scope.accountKey && scope.userId)),
        map(scope => ({
          accountKey: scope.accountKey,
          userId: scope.userId
        }) as PolarisMigrationScope),
        distinctUntilChanged((prev, current) =>
          prev.accountKey === current.accountKey && prev.userId === current.userId
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(scope => {
        void this.initializeScopeInBackground(scope);
      });
  }

  private async initializeScopeInBackground(scope: PolarisMigrationScope): Promise<void> {
    const scopeKey = `${scope.accountKey}:${scope.userId}`;
    if (scopeKey === this.initializedScopeKey || scopeKey === this.inFlightScopeKey) {
      return;
    }

    this.inFlightScopeKey = scopeKey;
    try {
      await this.polarisRuntimeFacadeService.initPolarisRuntime(scope);
      this.initializedScopeKey = scopeKey;
    } catch (err) {
      // Runtime init failures should not block app bootstrap.
      if (!environment.production) {
        console.error('Polaris runtime init failed:', err);
      }
    } finally {
      if (this.inFlightScopeKey === scopeKey) {
        this.inFlightScopeKey = null;
      }
    }
  }
}
